import React, { Component } from 'react'
import {
    Bold, ClassicEditor, Essentials, Paragraph, Font, Image, Italic, Alignment,
    Link, List, Subscript, Superscript, Text, Underline, UnlinkCommand, SourceEditing, GeneralHtmlSupport,
} from 'ckeditor5'
import * as R from 'ramda'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import i18n from 'i18next'

import 'ckeditor5/ckeditor5.css'
import '../../assets/css/ckeditor.css'
import MainMergeFields from './plugins/mainMergeFields'
import PipeAnswerMergeFields from './plugins/pipeAnswerMergeFields'
import HoverInfo from '../../UniformComponents/HoverInfo'

export default class CKeditor5 extends Component {
    plugins = {
        basic: [Essentials, Paragraph, Bold, Italic, Underline, Subscript, Superscript],
        survey: [
            Essentials, Paragraph, Bold, Italic, Underline, MainMergeFields, SourceEditing, Link, GeneralHtmlSupport,
        ],
        surveyExit: [
            Essentials, Paragraph, Bold, Italic, Underline, MainMergeFields, SourceEditing, Link, GeneralHtmlSupport,
        ],
        default: [
            Essentials, Paragraph, Bold, Italic, Underline,
            Subscript, List, Superscript, Link, UnlinkCommand,
        ],
        emailSchedule: [
            Font, Bold, Italic, Underline, Text, List, SourceEditing,
            Link, Image, Alignment, Essentials, Paragraph, MainMergeFields, GeneralHtmlSupport,
        ],
        questionEditor: [
            Font, Bold, Italic, Underline, Subscript, Superscript, Text, List, SourceEditing,
            Link, Image, Essentials, Paragraph, MainMergeFields, PipeAnswerMergeFields,
        ],
    }

    toolbars = {
        basic: {
            items: ['bold', 'italic'],
        },
        survey: {
            items: ['bold', 'italic', 'underline', '|', 'sourceEditing', '|', 'mainMergefield', '|', 'link'],
        },
        surveyExit: {
            items: ['bold', 'italic', 'underline', '|', 'sourceEditing', '|', 'mainMergefield', '|', 'link'],
        },
        default: {
            items: ['bold', 'italic'],
        },
        emailSchedule: {
            shouldNotGroupWhenFull: true,
            items: ['fontFamily', 'fontSize', '|', 'bold', 'italic', 'underline', '|', 'fontColor', '|', 'alignment', '|', 'link', '|', 'numberedList', 'bulletedList', '|', 'sourceEditing', '|', 'mainMergefield'],
        },
        questionEditor: {
            shouldNotGroupWhenFull: true,
            items: ['bold', 'italic', 'underline', 'subscript', 'superscript', '|', 'link', '|', 'numberedList', 'bulletedList', '|', 'sourceEditing', '|', 'mainMergefield', 'pipeAnswerMergefield'],
        },
    }

    constructor(props) {
        super(props)
        this.editorRef = React.createRef()
        this.state = {
            warningIconStyle: {},
        }
    }

    replaceSourceLabel = (editor) => {
        const { _items: toolbarItems } = editor.ui.view.toolbar.items
        const sourceButton = toolbarItems.find((i) => i.class === 'ck-source-editing-button')
        if (sourceButton) sourceButton.set('label', i18n.t('CKeditor.source.label'))
    }

    updateWarningIconPosition = () => {
        const { warningMsg } = this.props
        if (!warningMsg || !this.editorRef.current) return

        const toolbar = this.editorRef.current.querySelector('.ck-editor__top')
        if (toolbar) {
            const toolbarHeight = toolbar.offsetHeight
            const topPosition = toolbarHeight + 10
            this.setState({
                warningIconStyle: {
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: '8px',
                    zIndex: 2,
                },
            })
        }
    }

    onReady = (editor, onSourceToggle) => {
        this.replaceSourceLabel(editor)
        const sourceEditingPlugin = editor.plugins.get('SourceEditing')

        sourceEditingPlugin.on('change:isSourceEditingMode', (evt, name, isSourceMode) => {
            onSourceToggle(isSourceMode)
        })
        editor.editing.view.document.on('enter', (evt, data) => {
            data.preventDefault()
            evt.stop()

            if (data.isSoft) {
                editor.execute('enter')
                editor.editing.view.scrollToTheSelection()

                return
            }

            editor.execute('shiftEnter')
            editor.editing.view.scrollToTheSelection()
        }, { priority: 'high' })

        this.updateWarningIconPosition()
    }

    render() {
        const {
            toolbar, store, data, onBlur, placeholder, onSourceToggle, locales, warningMsg,
        } = this.props
        const { warningIconStyle } = this.state

        return (
            <div ref={this.editorRef} style={{ position: 'relative' }}>
                {warningMsg && (
                    <div style={warningIconStyle}>
                        <HoverInfo
                            cssClass="warning-icon dynamic-position"
                            desc={warningMsg}
                            dir="right"
                        >
                            <i className="fa-regular fa-language warning" />
                        </HoverInfo>
                    </div>
                )}
                <CKEditor
                    editor={ClassicEditor}
                    config={{
                        plugins: this.plugins[toolbar],
                        toolbar: this.toolbars[toolbar],
                        htmlSupport: {
                            allow: [
                                {
                                    name: /.*/,
                                    attributes: ['href', 'target', 'src', 'alt'],
                                    styles: true,
                                    classes: true,
                                },
                            ],
                        },
                        link: {
                            defaultProtocol: 'https://',
                            decorators: {
                                openInNewTab: {
                                    mode: 'manual',
                                    label: i18n.t('CKeditor.link.openNewTab'),
                                    defaultValue: true,
                                    attributes: {
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                    },
                                },
                            },
                        },
                        placeholder,
                        custom: {
                            locales: locales ?? R.pathOr([], ['survey', 'metadata', 'locales'], store),
                            clientLocales: R.pathOr([], ['clientLocales'], store),
                            gatewayUrl: R.pathOr('', ['http', 'gatewayUrl'], store),
                            mergeFields: R.pathOr([], ['mergeFields'], store),
                            toolbar,
                        },
                    }}
                    data={data}
                    onBlur={(event, editor) => onBlur(editor.getData())}
                    onReady={(editor) => this.onReady(editor, onSourceToggle)}
                />
            </div>
        )
    }
}
